import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import {
  Modal,
  View,
  StyleSheet,
  Pressable,
  ScrollView,
  useWindowDimensions,
  Platform,
  Animated,
} from 'react-native';
import Text from '../common/Text';
import { useTheme } from '../../hooks/useTheme';
import FloatingActionButton from './FloatingActionButton';
import {
  AI_COLOR_CHAT_PROMPT_SECTIONS,
  AIColorChatPromptSectionKey,
  AIColorPromptSeason,
} from '../../data/aiColorChatPrompts';
import { useUser } from '../../hooks/useAuth';
import { getAIChatReply } from '../../api/colours';

const ENABLE_AI_FEATURES = false;

export interface PremiumAIColorChatModalProps {
  visible: boolean;
  onClose: () => void;
  /** FAB position (match the screen FAB so it feels like the same button). */
  fabBottom: number;
  fabRight: number;
  title?: string;
  promptSectionKeys?: AIColorChatPromptSectionKey[];
  detectedSeason?: string | null;
  undertone?: string | null;
  skinToneCategory?: string | null;
  dominantColors?: Array<{ hex: string }> | null;
}

/**
 * Read-only chat-style modal shell. No dimmed backdrop; panel animates in like a popup.
 */
const PremiumAIColorChatModal: React.FC<PremiumAIColorChatModalProps> = ({
  visible,
  onClose,
  fabBottom,
  fabRight,
  title: titleProp,
  promptSectionKeys,
  detectedSeason,
  undertone,
  skinToneCategory,
  dominantColors,
}) => {
  const { colors } = useTheme();
  const { data: user } = useUser();
  const resolvedTitle = titleProp ?? `Hi ${user?.name ?? 'there'}!`;
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const isDesktopWeb = Platform.OS === 'web' && windowWidth >= 1024;
  const desktopPanelWidth = Math.max(520, Math.min(windowWidth * 0.5, 920));
  /** Keep chat panel above the FAB so content is not obscured */
  const panelBottomMargin = fabBottom + 56 + 12;
  const maxPanelHeight = isDesktopWeb
    ? Math.min(windowHeight * 0.78, 760)
    : Math.min(windowHeight * 0.8, 680);
  /** Fill most of the panel so the shell looks tall even before content loads */
  const bodyMinHeight = Math.max(260, maxPanelHeight - 110);

  const [renderModal, setRenderModal] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<string | null>(null);
  const [isLoadingReply, setIsLoadingReply] = useState(false);
  const [kimiReply, setKimiReply] = useState<string | null>(null);
  const [kimiError, setKimiError] = useState<string | null>(null);
  const displayError = useMemo(
    () => kimiError?.replace(/\bkimi\b/gi, 'AI assistant') ?? null,
    [kimiError]
  );
  const typingDot1 = useMemo(() => new Animated.Value(0), []);
  const typingDot2 = useMemo(() => new Animated.Value(0), []);
  const typingDot3 = useMemo(() => new Animated.Value(0), []);
  const opacity = useMemo(() => new Animated.Value(0), []);
  const translateY = useMemo(() => new Animated.Value(28), []);
  const translateX = useMemo(() => new Animated.Value(0), []);
  const scale = useMemo(() => new Animated.Value(0.94), []);

  const normalizedSeason = useMemo<AIColorPromptSeason | null>(() => {
    const value = detectedSeason?.toLowerCase().trim();
    if (!value) return null;
    if (value.includes('spring')) return 'spring';
    if (value.includes('summer')) return 'summer';
    if (value.includes('autumn') || value.includes('fall')) return 'autumn';
    if (value.includes('winter')) return 'winter';
    return null;
  }, [detectedSeason]);

  const promptSections = useMemo(() => {
    const cols = isDesktopWeb ? 3 : 2;
    const gapPx = 14;
    const cardsScrollPadding = 16 * 2; // paddingHorizontal: 16
    const panelWidth = isDesktopWeb ? desktopPanelWidth : windowWidth - 32;
    const contentWidth = Math.max(260, panelWidth - cardsScrollPadding);
    const promptWidthPx = (contentWidth - gapPx * (cols - 1)) / cols;

    const visibleSections = promptSectionKeys?.length
      ? AI_COLOR_CHAT_PROMPT_SECTIONS.filter((section) =>
          promptSectionKeys.includes(section.key)
        )
      : AI_COLOR_CHAT_PROMPT_SECTIONS;

    return visibleSections.map((section) => ({
      ...section,
      prompts: normalizedSeason ? section.promptsBySeason[normalizedSeason] : [],
      promptWidth: promptWidthPx,
    }));
  }, [
    isDesktopWeb,
    desktopPanelWidth,
    normalizedSeason,
    promptSectionKeys,
    windowWidth,
  ]);

  // Keep modal mounted after open so close animation can run (exit sets renderModal false).
  useLayoutEffect(() => {
    if (visible) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sync mount flag for exit animation lifecycle
      setRenderModal(true);
    }
  }, [visible]);

  /** Popup open: spring scale + slide from bottom / bottom-right */
  useEffect(() => {
    if (!visible) return;
    opacity.setValue(0);
    translateY.setValue(28);
    translateX.setValue(isDesktopWeb ? 14 : 0);
    scale.setValue(0.94);
    Animated.parallel([
      Animated.spring(opacity, {
        toValue: 1,
        useNativeDriver: true,
        tension: 68,
        friction: 11,
      }),
      Animated.spring(translateY, {
        toValue: 0,
        useNativeDriver: true,
        tension: 68,
        friction: 11,
      }),
      Animated.spring(translateX, {
        toValue: 0,
        useNativeDriver: true,
        tension: 68,
        friction: 11,
      }),
      Animated.spring(scale, {
        toValue: 1,
        useNativeDriver: true,
        tension: 68,
        friction: 11,
      }),
    ]).start();
  }, [visible, isDesktopWeb, opacity, scale, translateX, translateY]); // Animated.Value instances are stable (useMemo)

  /** Popup close */
  useEffect(() => {
    if (visible || !renderModal) return;
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 0,
        duration: 180,
        useNativeDriver: true,
      }),
      Animated.timing(translateY, {
        toValue: 20,
        duration: 180,
        useNativeDriver: true,
      }),
      Animated.timing(translateX, {
        toValue: isDesktopWeb ? 10 : 0,
        duration: 180,
        useNativeDriver: true,
      }),
      Animated.timing(scale, {
        toValue: 0.96,
        duration: 180,
        useNativeDriver: true,
      }),
    ]).start(({ finished }) => {
      if (finished) setRenderModal(false);
    });
  }, [
    visible,
    renderModal,
    isDesktopWeb,
    opacity,
    scale,
    translateX,
    translateY,
  ]);

  useEffect(() => {
    if (!isLoadingReply) {
      typingDot1.setValue(0);
      typingDot2.setValue(0);
      typingDot3.setValue(0);
      return;
    }

    const createDotLoop = (dot: Animated.Value, delayMs: number) =>
      Animated.loop(
        Animated.sequence([
          Animated.delay(delayMs),
          Animated.timing(dot, {
            toValue: -5,
            duration: 180,
            useNativeDriver: true,
          }),
          Animated.timing(dot, {
            toValue: 0,
            duration: 180,
            useNativeDriver: true,
          }),
          Animated.delay(220),
        ])
      );

    const loop1 = createDotLoop(typingDot1, 0);
    const loop2 = createDotLoop(typingDot2, 120);
    const loop3 = createDotLoop(typingDot3, 240);

    loop1.start();
    loop2.start();
    loop3.start();

    return () => {
      loop1.stop();
      loop2.stop();
      loop3.stop();
    };
  }, [isLoadingReply, typingDot1, typingDot2, typingDot3]);

  const handlePromptPress = useCallback(
    async (prompt: string) => {
      const trimmedPrompt = prompt.trim();
      if (!trimmedPrompt) return;

      setSelectedPrompt(trimmedPrompt);
      setKimiReply(null);
      setKimiError(null);

      if (!ENABLE_AI_FEATURES) {
        setKimiReply(
          'AI insights are temporarily unavailable in this production release. Please check your saved color analysis recommendations.'
        );
        return;
      }

      setIsLoadingReply(true);

      try {
        const response = await getAIChatReply({
          prompt: trimmedPrompt,
          undertone: undertone ?? undefined,
          skinToneCategory: skinToneCategory ?? undefined,
          dominantColors: dominantColors ?? undefined,
        });
        setKimiReply(response.reply.trim());
      } catch {
        setKimiError('Kimi could not answer right now. Please try again.');
      } finally {
        setIsLoadingReply(false);
      }
    },
    [dominantColors, skinToneCategory, undertone]
  );

  if (!visible && !renderModal) {
    return null;
  }

  return (
    <Modal
      visible
      transparent
      animationType="none"
      onRequestClose={onClose}
    >
      <View style={styles.root} pointerEvents="box-none">
        {/* Transparent: no gray overlay; tap outside still closes */}
        <Pressable
          style={styles.backdrop}
          onPress={onClose}
          accessibilityLabel="Dismiss"
        />

        <Animated.View
          style={[
            styles.panel,
            {
              alignSelf: isDesktopWeb ? 'flex-end' : 'stretch',
              marginLeft: isDesktopWeb ? 0 : 16,
              marginRight: 16,
              width: isDesktopWeb ? desktopPanelWidth : undefined,
              maxHeight: maxPanelHeight,
              marginBottom: panelBottomMargin,
              backgroundColor: colors.card,
              borderColor: colors.border,
              opacity,
              transform: [{ translateX }, { translateY }, { scale }],
              ...Platform.select({
                ios: {
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 8 },
                  shadowOpacity: 0.18,
                  shadowRadius: 16,
                },
                android: { elevation: 12 },
                web: {
                  boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                } as object,
                default: {},
              }),
            },
          ]}
        >
          <View
            style={[
              styles.header,
              { backgroundColor: colors.primary, borderBottomColor: colors.border },
            ]}
          >
            <Text style={[styles.headerTitle, { color: colors.background }]}>
              {resolvedTitle}
            </Text>
          </View>

          <ScrollView
            style={[styles.cardsScroll, { minHeight: bodyMinHeight }]}
            contentContainerStyle={styles.cardsGrid}
            showsVerticalScrollIndicator={false}
          >
            {promptSections.map((section) => (
              <View
                key={section.key}
                style={styles.section}
              >
                <View style={styles.sectionPromptGrid}>
                  {section.prompts.map((prompt) => (
                    <Pressable
                      key={`${section.key}-${prompt}`}
                      onPress={() => {
                        handlePromptPress(prompt);
                      }}
                      disabled={isLoadingReply}
                      style={({ pressed }) => [
                        styles.promptCard,
                        {
                          width: section.promptWidth,
                          backgroundColor: section.bg,
                          borderColor: colors.borderLight,
                          opacity: pressed ? 0.85 : 1,
                        },
                      ]}
                    >
                      <Text
                        style={styles.promptItem}
                        numberOfLines={2}
                        ellipsizeMode="tail"
                      >
                        {prompt}
                      </Text>
                    </Pressable>
                  ))}
                </View>
              </View>
            ))}

            <View
              style={ styles.replyCard }
            >
              {!selectedPrompt && (
                <Text style={[styles.placeholderText, { color: colors.secondary }]}>
                  Tap any prompt card above to ask the assistant.
                </Text>
              )}

              {isLoadingReply && (
                <View style={styles.typingRow}>
                  <View style={styles.typingDots}>
                    <Animated.Text
                      style={[
                        styles.typingDot,
                        { color: colors.text, transform: [{ translateY: typingDot1 }] },
                      ]}
                    >
                      .
                    </Animated.Text>
                    <Animated.Text
                      style={[
                        styles.typingDot,
                        { color: colors.text, transform: [{ translateY: typingDot2 }] },
                      ]}
                    >
                      .
                    </Animated.Text>
                    <Animated.Text
                      style={[
                        styles.typingDot,
                        { color: colors.text, transform: [{ translateY: typingDot3 }] },
                      ]}
                    >
                      .
                    </Animated.Text>
                  </View>
                </View>
              )}

              {!isLoadingReply && kimiError && (
                <View>
                  <Text style={[styles.replyError, { color: colors.errorText }]}>
                    {displayError}
                  </Text>
                  {!!selectedPrompt && (
                    <Pressable
                      onPress={() => {
                        handlePromptPress(selectedPrompt);
                      }}
                      style={[
                        styles.retryButton,
                        { borderColor: colors.primary, backgroundColor: colors.card },
                      ]}
                    >
                      <Text style={[styles.retryButtonText, { color: colors.primary }]}>
                        Retry
                      </Text>
                    </Pressable>
                  )}
                </View>
              )}

              {!isLoadingReply && !kimiError && !!kimiReply && (
                <Text style={[styles.replyBody, { color: colors.text }]}>{kimiReply}</Text>
              )}
            </View>
          </ScrollView>
        </Animated.View>

        <FloatingActionButton
          active
          bottom={fabBottom}
          right={fabRight}
          onPress={onClose}
          viewportFixed={false}
        />
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'transparent',
  },
  panel: {
    marginHorizontal: 16,
    borderRadius: 20,
    borderWidth: 1,
    overflow: 'hidden',
  },
  header: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  cardsScroll: {
    paddingHorizontal: 16,
  },
  cardsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    paddingBottom: 24,
    paddingTop: 14,
  },
  section: {
    width: '100%',
    marginBottom: 18,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '800',
    marginBottom: 10,
    lineHeight: 24,
  },
  sectionPromptGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  promptCard: {
    borderRadius: 14,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
  },
  promptItem: {
    color: '#4B2B2B',
    fontSize: 13,
    lineHeight: 18,
  },
  replyCard: {
    padding: 14,
    marginTop: 2,
  },
  selectedPromptText: {
    fontSize: 12,
    marginBottom: 8,
  },
  placeholderText: {
    fontSize: 13,
    lineHeight: 18,
  },
  replyBody: {
    fontSize: 13,
    lineHeight: 20,
  },
  typingRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
  typingDots: {
    flexDirection: 'row',
    marginLeft: 6,
    marginBottom: 2,
  },
  typingDot: {
    fontSize: 50,
    lineHeight: 24,
    marginHorizontal: 2,
    fontWeight: '700',
  },
  replyError: {
    fontSize: 13,
    lineHeight: 18,
  },
  retryButton: {
    alignSelf: 'flex-start',
    marginTop: 10,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  retryButtonText: {
    fontSize: 12,
    fontWeight: '600',
  },
});

export default PremiumAIColorChatModal;
